import { formatDate } from "_shared/formatting/dates";
import Guid from "_shared/guids/guid";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";

/** One column filter the table holds, said in words: "Name: Acme". */
export type ColumnFilterDescription = Readonly<{ columnId: string; text: string }>;

/**
 * Names a filter's value where the page can: a person's name for a user id, say. Undefined falls
 * back to the value itself, or, for an id, to the column alone.
 */
export type DescribeColumnFilterValue = (columnId: string, value: string) => string | undefined;

/**
 * Each column filter the table holds, as the reader would say it (#6714): the column's heading and
 * the value, so a filter is never hidden behind a closed filter row. An id is never shown: a value
 * the page cannot name is said as the column alone ("From"), as `WorkRecordCriteriaChips` says a
 * person the directory does not name.
 */
export function describeColumnFilters<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  describeValue?: DescribeColumnFilterValue,
): ColumnFilterDescription[] {
  const descriptions: ColumnFilterDescription[] = [];
  for (const filter of table.getState().columnFilters) {
    const heading = headingOf(table, filter.id);
    const values = valuesOf(filter.value).map((value) => describeValue?.(filter.id, value) ?? (isId(value) ? undefined : value));
    if (values.length === 0) continue;
    const named = values.filter((value): value is string => value !== undefined);
    descriptions.push({
      columnId: filter.id,
      text: named.length === values.length ? `${heading}: ${named.join(", ")}` : heading,
    });
  }
  return descriptions;
}

function headingOf<TData extends MRT_RowData>(table: MRT_TableInstance<TData>, columnId: string): string {
  const header = columnOf(table, columnId)?.columnDef.header;
  return typeof header === "string" && header.trim() !== "" ? header : columnId;
}

/** A filter's value as the strings a reader typed or chose; a range keeps the ends it has. */
function valuesOf(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(valuesOf);
  if (typeof value === "boolean") return [value ? "Yes" : "No"];
  if (typeof value === "number") return [value.toString()];
  if (typeof value === "string") return value.trim() === "" ? [] : [value];
  // A date reads as the list draws it: "Mar 3, 2027" (#6713).
  if (value instanceof Date) return [formatDate(value)].filter((text): text is string => text !== undefined);
  return [];
}

/** The column, or none, without the warning `getColumn` logs for an id the table does not have. */
export function columnOf<TData extends MRT_RowData>(table: MRT_TableInstance<TData>, columnId: string) {
  return table.getAllLeafColumns().find((column) => column.id === columnId);
}

function isId(value: string): boolean {
  return Guid.isValid(value);
}
